import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  Image,
  Loader,
  Select,
  Stack,
  Text,
  ThemeIcon
} from '@mantine/core';
import { IconSparkles, IconAffiliate, IconExternalLink, IconArrowRight, IconRefresh } from '@tabler/icons-react';
import { recommendationsAPI } from '../../services/api';

// Makes the graph/AI recommendation engine tangible on the dashboard instead
// of leaving it as a locked nav item. Calls the real /recommend endpoint
// (backend/routes/recommend.js) which hits OpenAI + eBay, so this is
// click-to-generate rather than auto-firing on every dashboard load.
const AiSpotlightCard = ({ personas }) => {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(String(personas[0]?.id ?? ''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const selectedPersona = personas.find(p => String(p.id) === String(selectedId));

  const generate = async () => {
    if (!selectedId) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await recommendationsAPI.getRecommendations({
        subject_type: 'persona',
        subject_id: selectedId,
        query: ''
      });
      const product = response.data.products?.[0];
      if (!product) {
        setError("Couldn't find a match this time — try again in a moment.");
      } else {
        setResult({
          product,
          reasoning: response.data.reasoning?.[0] || product.ai_reasoning || null,
          personaName: response.data.persona_name || selectedPersona?.name
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't generate a suggestion right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      radius="lg"
      p="lg"
      withBorder
      style={{
        borderColor: 'rgba(168,85,247,0.35)',
        background: 'linear-gradient(135deg, rgba(109,40,217,0.08) 0%, rgba(0,0,0,0) 60%)'
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
        <Group align="flex-start" gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 240 }}>
          <ThemeIcon size={52} radius="md" variant="light" color="violet" style={{ flexShrink: 0 }}>
            <IconAffiliate size={28} stroke={1.5} />
          </ThemeIcon>
          <Box style={{ minWidth: 0 }}>
            <Badge
              variant="light"
              color="violet"
              leftSection={<IconSparkles size={11} />}
              mb={6}
            >
              Graph-powered pick
            </Badge>

            {!result && !loading && (
              <>
                <Text fw={600} size="sm" mb={4}>
                  Let the graph engine surprise you
                </Text>
                <Text size="xs" c="dimmed" mb="sm">
                  Pulls traits and preferences from Neo4j to suggest gifts that keyword search would miss.
                </Text>
              </>
            )}

            {loading && (
              <Group gap="xs" mt={4} mb="sm">
                <Loader size="xs" color="violet" />
                <Text size="xs" c="dimmed">
                  Looking for something {selectedPersona?.name || 'they'} wouldn't expect...
                </Text>
              </Group>
            )}

            {error && (
              <Text size="xs" c="red" mt={4} mb="sm">
                {error}
              </Text>
            )}

            {result && (
              <Stack gap={6} mt={4}>
                <Group gap="sm" wrap="nowrap" align="flex-start">
                  {result.product.image_url ? (
                    <Image
                      src={result.product.image_url}
                      alt={result.product.name}
                      w={56}
                      h={56}
                      radius="md"
                      fit="cover"
                      style={{ flexShrink: 0 }}
                    />
                  ) : (
                    <ThemeIcon size={56} radius="md" variant="light" color="gray" style={{ flexShrink: 0 }}>
                      <IconSparkles size={24} />
                    </ThemeIcon>
                  )}
                  <Box style={{ minWidth: 0 }}>
                    <Text fw={600} size="sm" lineClamp={2}>{result.product.name}</Text>
                    {result.product.price > 0 && (
                      <Text size="xs" c="teal" fw={700}>${Number(result.product.price).toFixed(2)}</Text>
                    )}
                  </Box>
                </Group>
                {result.reasoning && (
                  <Text size="xs" c="dimmed" fs="italic" mt={2}>
                    "{result.reasoning}"
                  </Text>
                )}
              </Stack>
            )}
          </Box>
        </Group>

        {personas.length > 1 && !result && (
          <Select
            data={personas.map(p => ({ value: String(p.id), label: p.name }))}
            value={selectedId}
            onChange={setSelectedId}
            allowDeselect={false}
            size="xs"
            w={160}
          />
        )}
      </Group>

      <Group gap="xs" mt="md">
        {!result ? (
          <Button
            size="xs"
            variant="gradient"
            gradient={{ from: 'violet', to: 'purple', deg: 45 }}
            onClick={generate}
            loading={loading}
            disabled={!selectedId}
          >
            Get an AI pick{selectedPersona ? ` for ${selectedPersona.name}` : ''}
          </Button>
        ) : (
          <>
            {result.product.url && (
              <Button
                size="xs"
                variant="light"
                color="violet"
                component="a"
                href={result.product.url}
                target="_blank"
                rel="noopener noreferrer"
                rightSection={<IconExternalLink size={13} />}
              >
                View item
              </Button>
            )}
            <Button
              size="xs"
              variant="subtle"
              color="violet"
              rightSection={<IconArrowRight size={13} />}
              onClick={() => navigate(`/products?persona=${selectedId}`)}
            >
              See more picks for {result.personaName}
            </Button>
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              leftSection={<IconRefresh size={13} />}
              onClick={generate}
              loading={loading}
            >
              Try another
            </Button>
          </>
        )}
      </Group>
    </Card>
  );
};

export default AiSpotlightCard;
